import LegalShell from "./LegalShell";

export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" updated="10 February 2026" path="/legal/privacy" description="Viyora Privacy Policy — DPDP-aligned, sub-processor disclosure, patient data retention, and grievance officer contact.">
      <p>This Privacy Policy explains how <strong>Viyora Tech</strong> (“we”, “our”, “us”) collects, stores, processes and protects personal information when you use Viyora, our cloud-based clinic management platform.</p>

      <p>Viyora is developed and operated by Viyora Tech.</p>

      <p>This Privacy Policy complies with the Digital Personal Data Protection Act, 2023 (India), the Information Technology Act, 2000 and other applicable laws.</p>

      <p>Throughout this policy:</p>
      <ul>
        <li><strong>Viyora</strong> refers to the software platform.</li>
        <li><strong>Viyora Tech</strong> refers to the legal business operating the platform.</li>
      </ul>

      <h2>1. Data we collect</h2>

      <h3>a) Account & clinic data</h3>
      <ul>
        <li>Name, work email, phone, clinic name, GSTIN, PAN, business address</li>
        <li>Doctor / staff role, licensing state, working hours</li>
        <li>Billing address and Razorpay-issued payment identifiers (we do <strong>not</strong> store card numbers or CVV)</li>
      </ul>

      <h3>b) Patient records (uploaded by clinics)</h3>
      <ul>
        <li>Name, contact details, date of birth, gender</li>
        <li>Medical history, allergies, special conditions, dental charts, treatment notes</li>
        <li>X-ray images (when uploaded), consent form signatures, warranty cards</li>
        <li>Appointment history, invoices, prescriptions</li>
      </ul>

      <h3>c) Technical data</h3>
      <ul>
        <li>IP address, browser, device, and OS metadata</li>
        <li>Session cookies (essential only; no tracking cookies without consent)</li>
        <li>Product analytics via PostHog (anonymised where possible)</li>
      </ul>

      <h2>2. How we use your data</h2>
      <ul>
        <li>To provide, maintain, and improve the Service</li>
        <li>To generate AI-assisted clinical suggestions using Anthropic Claude (via the Emergent LLM proxy — data is not used to train third-party models)</li>
        <li>To send appointment reminders, consent links, and warranty notifications via WhatsApp (Gupshup) on your clinic’s behalf</li>
        <li>To process subscription payments through Razorpay</li>
        <li>To detect fraud, abuse, and enforce our Terms</li>
        <li>To comply with legal, tax, and regulatory obligations</li>
      </ul>

      <h2>3. Legal basis (DPDP Act, 2023)</h2>
      <p>We process personal data on the basis of your consent, contractual necessity, and legitimate purposes as defined under Section 7 of the DPDP Act. For patient records uploaded by clinics, the clinic acts as the Data Fiduciary and warrants that consent has been obtained from the patient. Viyora acts as a Data Processor and processes such data solely on the clinic’s instructions.</p>

      <h2>4. Data sharing & sub-processors</h2>
      <p>We do not sell your personal data. We share it only with vetted sub-processors under written data-processing agreements:</p>
      <ul>
        <li><strong>Razorpay</strong> — payment processing (PCI-DSS compliant)</li>
        <li><strong>Gupshup</strong> — WhatsApp message delivery (Meta BSP)</li>
        <li><strong>Anthropic (via Emergent LLM proxy)</strong> — clinical AI reasoning (data not retained for model training)</li>
        <li><strong>Supabase</strong> — authentication and social login</li>
        <li><strong>MongoDB Atlas / Cloud Kubernetes hosting</strong> — primary database and application hosting (India / Asia region preferred)</li>
      </ul>
      <p>Where cross-border transfer occurs, we rely on the standard contractual clauses and the whitelist mechanism under the DPDP Act.</p>

      <h2>5. Data retention</h2>
      <ul>
        <li>Active clinic account: retained for as long as your subscription is active.</li>
        <li>After account closure: exported on request, then permanently deleted within 30 days.</li>
        <li>Financial records (invoices, GST) are retained for 8 years as required by Section 36 of the CGST Act.</li>
        <li>Backup snapshots are rotated on a 30-day cycle.</li>
      </ul>

      <h2>6. Data security</h2>
      <ul>
        <li>TLS 1.2+ encryption in transit; AES-256 at rest for databases and backups.</li>
        <li>Bcrypt-hashed passwords with per-user salts. JWT session tokens.</li>
        <li>Row-level tenant isolation via <code>clinic_id</code>-scoped queries and MongoDB compound indexes.</li>
        <li>Role-based access control (Admin, Doctor, Receptionist).</li>
        <li>Razorpay webhook and payment signatures verified via HMAC-SHA256.</li>
        <li>Least-privilege access to production systems. Access reviews every 90 days.</li>
      </ul>

      <h2>7. Your rights (Data Principal rights)</h2>
      <p>Under the DPDP Act you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Request correction or update of inaccurate data.</li>
        <li>Request erasure (subject to legal retention requirements).</li>
        <li>Withdraw consent at any time. Withdrawal does not affect prior lawful processing.</li>
        <li>Nominate an heir to receive your data in the event of death or incapacity.</li>
        <li>File a grievance with our Data Protection Officer.</li>
      </ul>

      <h2>8. Grievance officer</h2>
      <p>In accordance with Section 8(9) of the DPDP Act, our Grievance Officer is:</p>
      <p>
        Viyora Tech<br />
        Email: <a href="mailto:admin@viyora.co.in">admin@viyora.co.in</a><br />
        Response time: within 15 working days
      </p>

      <h2>9. Children’s data</h2>
      <p>Where a patient is a minor (below 18), the clinic must obtain verifiable consent from a parent or lawful guardian before uploading records to Viyora, in line with Section 9 of the DPDP Act.</p>

      <h2>10. Changes to this policy</h2>
      <p>We will notify you by email at least 14 days before material changes take effect. The date at the top of this page indicates the most recent revision.</p>

      <h2>11. Google API user data &amp; Limited Use</h2>
      <p>
        Where a doctor connects a Google Calendar to Viyora, we request the
        narrow scopes <code>calendar.events</code> and{" "}
        <code>calendar.calendarlist.readonly</code> only. Viyora&rsquo;s use
        and transfer of information received from Google APIs to any other app
        will adhere to the{" "}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. Google user data is never
        used for advertising and never used to train AI/ML models. See our{" "}
        <a href="/legal/limited-use">Limited Use disclosure</a> for the full
        breakdown, retention rules, and revocation instructions.
      </p>

      <h2>12. Contact</h2>
      <p>For any privacy-related questions or requests, please contact:</p>
      <p>
        Viyora Tech<br />
        Email: <a href="mailto:admin@viyora.co.in">admin@viyora.co.in</a><br />
        Website: <a href="https://viyora.co.in">https://viyora.co.in</a><br />
        Phone: <a href="tel:+919810453892">+91 9810453892</a>
      </p>
    </LegalShell>
  );
}
